/**
 * graph-layout.ts — Pure layout function for the InfluenceGraphView.
 *
 * Ported from ed-tool-philosophy: timeline cronologica orizzontale +
 * beeswarm verticale, with data-driven piecewise linear X-axis (epochs)
 * and Bézier-curve edges colored per relation type.
 *
 * X axis: data-driven piecewise linear scale. Epochs computed from the
 * actual author distribution with boundaries at natural literary breakpoints
 * (1500, 1700, 1900) and weights mixing 60% density + 40% span — so dense
 * eras (Victorian) get more space, empty stretches don't waste room.
 *
 * Y axis: beeswarm — nodes start at vertical center and spiral outward
 * greedily until they don't collide with previously placed nodes. Stable
 * iff input is sorted by birth_year.
 *
 * Edges: from 5 typed relation arrays (influenced_by, influenced,
 * contemporary_with, responds_to, adapts), kept directed (semantics differ
 * per kind) and rendered as Bézier curves colored per kind.
 */

import type { AuthorCatalog, AuthorRelations } from '@/data/schema';

export type RelationKind = keyof AuthorRelations;

export interface GraphNode {
  id: string;
  name: string;
  displayName: string;
  x: number;
  y: number;
  year: number;
  /** First period of the author — drives node accent color. */
  period: string | null;
  /** First movement — drives node secondary marker. */
  movement: string | null;
}

export interface GraphEdge {
  sourceId: string;
  targetId: string;
  kind: RelationKind;
  sx: number;
  sy: number;
  tx: number;
  ty: number;
}

export interface GraphAxisTick {
  year: number;
  x: number;
  label: string;
}

export interface GraphLayout {
  nodes: GraphNode[];
  edges: GraphEdge[];
  bounds: { width: number; height: number };
  axisTicks: GraphAxisTick[];
}

export interface LayoutOptions {
  width: number;
  height: number;
  padding: { top: number; right: number; bottom: number; left: number };
  nodeRadius: number;
}

/** Stroke colors per relation type — used both in SVG and legend. */
export const RELATION_COLORS: Record<RelationKind, string> = {
  influenced_by:     '#7a1f2b', // burgundy — primary
  influenced:        '#b87a2a', // ochre
  contemporary_with: '#4a4640', // dark warm gray
  responds_to:       '#1d5b6b', // teal
  adapts:            '#5b3e7a', // muted purple
};

export const RELATION_ORDER: ReadonlyArray<RelationKind> = [
  'influenced_by',
  'influenced',
  'contemporary_with',
  'responds_to',
  'adapts',
];

interface Epoch {
  from: number;
  to: number;
  weight: number;
}

/** Natural literary breakpoints used to split the timeline into epochs. */
const HISTORICAL_BREAKS = [1500, 1700, 1900];

/** Density vs span balance: 0 = pure span, 1 = pure density. */
const DENSITY_WEIGHT = 0.6;

/**
 * Compute a compact display name. Most English-lit authors are 2-3 tokens
 * (first + last); we keep the last name. A few canonical cases that should
 * stay as the full form are listed in `keepFull`.
 */
export function computeDisplayName(name: string): string {
  const trim = name.trim();

  const keepFull = [
    /gawain\s+poet/i,
    /sir\s+thomas/i,
    /henry\s+james/i,        // disambiguate from William James (philosophy)
    /william\s+james/i,
  ];
  for (const re of keepFull) {
    if (re.test(trim)) return trim;
  }

  const tokens = trim.split(/\s+/);
  if (tokens.length === 1) return trim;
  // Skip honorifics like "Sir", "Lord"
  if (/^(sir|lord|lady|dr)\.?$/i.test(tokens[0])) {
    return tokens.slice(1).join(' ');
  }
  return tokens[tokens.length - 1];
}

function computeEpochs(years: number[]): Epoch[] {
  if (years.length === 0) return [{ from: 700, to: 2050, weight: 1 }];

  const sorted = [...years].sort((a, b) => a - b);
  const minY = sorted[0] - 30;
  const maxY = sorted[sorted.length - 1] + 30;

  const breaks = HISTORICAL_BREAKS.filter((b) => b > minY && b < maxY);
  const boundaries = [minY, ...breaks, maxY];

  const totalAuthors = years.length;
  const totalSpan = maxY - minY;
  const epochs: Epoch[] = [];

  for (let i = 0; i < boundaries.length - 1; i++) {
    const from = boundaries[i];
    const to = boundaries[i + 1];
    const count = years.filter((y) => y >= from && y < to).length;
    const span = to - from;

    const densityPart = totalAuthors > 0 ? count / totalAuthors : 0;
    const spanPart = totalSpan > 0 ? span / totalSpan : 0;
    const weight = DENSITY_WEIGHT * densityPart + (1 - DENSITY_WEIGHT) * spanPart;

    epochs.push({ from, to, weight: Math.max(weight, 0.02) });
  }

  const wSum = epochs.reduce((s, e) => s + e.weight, 0);
  for (const e of epochs) e.weight /= wSum;

  return epochs;
}

function yearToRatio(year: number, epochs: Epoch[]): number {
  let cumStart = 0;
  for (const e of epochs) {
    if (year <= e.to) {
      const clamped = Math.max(year, e.from);
      const local = (clamped - e.from) / (e.to - e.from);
      return cumStart + local * e.weight;
    }
    cumStart += e.weight;
  }
  return 1;
}

function computeAxisTicks(
  epochs: Epoch[],
  plotLeft: number,
  plotWidth: number,
): GraphAxisTick[] {
  const minY = epochs[0].from;
  const maxY = epochs[epochs.length - 1].to;

  // Every 200 years pre-1500, every 50 years 1500+
  const candidates: number[] = [];
  for (let y = Math.ceil(minY / 200) * 200; y < 1500 && y <= maxY; y += 200) {
    if (y >= minY) candidates.push(y);
  }
  for (let y = 1500; y <= maxY; y += 50) {
    if (y >= minY && !candidates.includes(y)) candidates.push(y);
  }

  return candidates
    .filter((y) => y >= minY && y <= maxY)
    .sort((a, b) => a - b)
    .map((year) => ({
      year,
      x: plotLeft + yearToRatio(year, epochs) * plotWidth,
      label: String(year),
    }));
}

export function computeLayout(
  authors: AuthorCatalog[],
  options: LayoutOptions,
): GraphLayout {
  const { width, height, padding, nodeRadius } = options;
  const plotWidth = width - padding.left - padding.right;
  const plotHeight = height - padding.top - padding.bottom;
  const centerY = padding.top + plotHeight / 2;
  const minDist = nodeRadius * 2.3;

  // Only dated authors are placeable on the timeline.
  const dated = authors.filter(
    (a): a is AuthorCatalog & { birth_year: number } => typeof a.birth_year === 'number',
  );

  if (dated.length === 0) {
    return { nodes: [], edges: [], bounds: { width, height }, axisTicks: [] };
  }

  const years = dated.map((a) => a.birth_year);
  const epochs = computeEpochs(years);

  const sorted = [...dated].sort((a, b) => a.birth_year - b.birth_year);

  const placed: GraphNode[] = [];
  const idToNode = new Map<string, GraphNode>();

  for (const a of sorted) {
    const ratio = yearToRatio(a.birth_year, epochs);
    const x = padding.left + ratio * plotWidth;

    let y = centerY;
    const maxOffset = plotHeight / 2 - nodeRadius;
    const step = minDist * 0.6;
    const candidates: number[] = [centerY];
    for (let offset = step; offset <= maxOffset; offset += step) {
      candidates.push(centerY + offset);
      candidates.push(centerY - offset);
    }
    for (const cy of candidates) {
      let collision = false;
      for (const p of placed) {
        const dx = p.x - x;
        const dy = p.y - cy;
        if (dx * dx + dy * dy < minDist * minDist) {
          collision = true;
          break;
        }
      }
      if (!collision) { y = cy; break; }
    }

    const node: GraphNode = {
      id: a.id,
      name: a.name,
      displayName: computeDisplayName(a.name),
      x,
      y,
      year: a.birth_year,
      period: a.periods[0] ?? null,
      movement: a.movements[0] ?? null,
    };
    placed.push(node);
    idToNode.set(a.id, node);
  }

  // Edges: 5 relation kinds, kept directed. We deduplicate the SAME
  // (source, target, kind) triple but keep different kinds between the same
  // pair (so e.g. "influenced + responds_to" both render).
  const seen = new Set<string>();
  const edges: GraphEdge[] = [];
  for (const a of authors) {
    if (!idToNode.has(a.id)) continue;
    for (const kind of RELATION_ORDER) {
      for (const targetId of a.relations[kind] ?? []) {
        if (!idToNode.has(targetId)) continue;
        const key = `${kind}|${a.id}|${targetId}`;
        if (seen.has(key)) continue;
        seen.add(key);
        const src = idToNode.get(a.id)!;
        const tgt = idToNode.get(targetId)!;
        edges.push({
          sourceId: src.id,
          targetId: tgt.id,
          kind,
          sx: src.x,
          sy: src.y,
          tx: tgt.x,
          ty: tgt.y,
        });
      }
    }
  }

  const axisTicks = computeAxisTicks(epochs, padding.left, plotWidth);

  return {
    nodes: placed,
    edges,
    bounds: { width, height },
    axisTicks,
  };
}
